const DEFAULT_ENGAGEMENT_EVENTS = ['scroll', 'click', 'touchstart', 'keydown'];

/**
 * Runs callback after the first user engagement or after fallbackMs (whichever comes first).
 * Lab tools (PageSpeed/Lighthouse) do not simulate interaction, so third-party scripts
 * load after the TBT measurement window when fallbackMs is high enough (~10s).
 */
export function deferUntilEngaged(callback, options = {}) {
  const {
    fallbackMs = 10000,
    idleTimeoutMs = 2000,
    events = DEFAULT_ENGAGEMENT_EVENTS
  } = options;

  if (typeof window === 'undefined') {
    return () => {};
  }

  let ran = false;

  function onEngage() {
    if (ran) return;
    ran = true;

    events.forEach(eventName => {
      window.removeEventListener(eventName, onEngage, true);
    });

    const execute = () => callback();
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(execute, { timeout: idleTimeoutMs });
    } else {
      window.setTimeout(execute, 0);
    }
  }

  const startFallbackTimer = () => {
    window.setTimeout(() => {
      if (!ran) onEngage();
    }, fallbackMs);
  };

  events.forEach(eventName => {
    window.addEventListener(eventName, onEngage, {
      passive: true,
      capture: true
    });
  });

  if (document.readyState === 'complete') {
    startFallbackTimer();
  } else {
    window.addEventListener('load', startFallbackTimer, { once: true });
  }

  return onEngage;
}

export function injectScript(src, id) {
  if (typeof document === 'undefined' || !src) return;
  if (id && document.getElementById(id)) return;

  const script = document.createElement('script');
  script.async = true;
  script.src = src;
  if (id) script.id = id;
  document.head.appendChild(script);
}
